/**
 * Formatiranje brojki dnevnog izvještaja. Ekran i ispis dijele ove funkcije
 * kako bi PDF i tablica prikazali identičnu vrijednost do zadnje decimale.
 */

import { dateLocale } from "../../../i18n/locale";

export const fmtInt = (value: number): string =>
  Math.round(value || 0).toLocaleString(dateLocale());

export const fmtDec = (value: number, digits = 2): string =>
  (value || 0).toLocaleString(dateLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export const fmtEur = (value: number): string => `${fmtDec(value)} €`;

export const fmtKg = (value: number): string => `${fmtDec(value, 1)} kg`;

export const fmtPct = (value: number, digits = 1): string => `${fmtDec(value, digits)} %`;

/** Predznak je nositelj informacije u kolonama odstupanja, pa se uvijek ispisuje. */
export const fmtSignedInt = (value: number): string => {
  const rounded = Math.round(value || 0);
  return `${rounded > 0 ? "+" : ""}${rounded.toLocaleString(dateLocale())}`;
};

export const fmtSignedEur = (value: number): string =>
  `${value > 0 ? "+" : ""}${fmtDec(value)} €`;

export const fmtSignedPct = (value: number, digits = 1): string =>
  `${value > 0 ? "+" : ""}${fmtDec(value, digits)} %`;

/** "YYYY-MM-DD" -> "08.09.2026." */
export const fmtDateHr = (iso: string): string => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!parts) return iso || "-";
  return `${parts[3]}.${parts[2]}.${parts[1]}.`;
};

/** "YYYY-MM-DD HH:MM:SS" -> "HH:MM" (bez tumačenja vremenske zone). */
export const fmtClock = (stamp: string | null, fallback = "--:--"): string => {
  if (!stamp) return fallback;
  const match = /(\d{2}):(\d{2})/.exec(stamp);
  return match ? `${match[1]}:${match[2]}` : fallback;
};

/** "YYYY-MM-DD HH:MM:SS" -> "08.09. 05:30" — zaglavlje izvještaja. */
export const fmtDayClock = (stamp: string | null, fallback = "-"): string => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(stamp || "");
  if (!parts) return fallback;
  return `${parts[3]}.${parts[2]}. ${parts[4]}:${parts[5]}`;
};

/** Minute od ponoći -> "HH:MM", za os grafa. */
export const minutesToClock = (minutes: number): string => {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** Zeleno kada je odstupanje u korist proizvodnje, crveno kada nije. */
export const diffClass = (value: number, positiveIsGood = true): string => {
  if (value === 0) return "diff-flat";
  const good = positiveIsGood ? value > 0 : value < 0;
  return good ? "diff-pos" : "diff-neg";
};
